import React from 'react'
import PropTypes from 'prop-types'
import { DataGrid } from '@mui/x-data-grid'
import { Box, Button, Paper, Chip } from '../../../../../components/mui/Core/index'
import { useTheme } from '../../../../../components/mui/Styles/index'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import LockIcon from '@mui/icons-material/Lock'

function HoldDetailsTable({ holds, onRelease, isSubmitting }) {
  const theme = useTheme()

  const rows = holds.map((h) => ({
    id: h.holdId,
    holdId: h.holdId,
    holdCode: h.holdCode,
    holdCategory: h.holdCategory,
    description: h.description,
    releasable: h.releasable,
    released: h.released,
    appliedTo: h.appliedTo,
    heldBy: h.heldBy,
    appliedAt: h.appliedAt,
    releasedBy: h.releasedBy,
    releasableRoles: h.releasableRoles,
  }))

  const columns = [
    {
      field: 'holdCode',
      headerName: 'Code',
      flex: 0.4,
      minWidth: 80,
      renderCell: (p) => (
        <Box sx={{ fontFamily: 'AtlasGroteskRegular', fontWeight: 600, fontSize: '0.85rem', color: '#cc3f0c' }}>
          {p.value}
        </Box>
      ),
    },
    { field: 'holdCategory', headerName: 'Category', flex: 0.5, minWidth: 100 },
    { field: 'description', headerName: 'Description', flex: 1.2, minWidth: 200 },
    {
      field: 'released',
      headerName: 'Status',
      flex: 0.5,
      minWidth: 100,
      renderCell: (p) => (
        p.value
          ? <Chip label='Released' size='small' sx={{ backgroundColor: '#e8f5e9', color: '#2a7b5a', fontFamily: 'AtlasGroteskRegular', fontSize: '0.72rem' }} />
          : <Chip label='Active Hold' size='small' sx={{ backgroundColor: '#fdecea', color: '#cc3f0c', fontFamily: 'AtlasGroteskRegular', fontSize: '0.72rem' }} />
      ),
    },
    { field: 'heldBy', headerName: 'Held By', flex: 0.4, minWidth: 80 },
    {
      field: 'appliedAt',
      headerName: 'Applied At',
      flex: 0.6,
      minWidth: 130,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (value) => value ? new Date(value).toLocaleDateString('en-US') : '—',
    },
    {
      field: 'releasedBy',
      headerName: 'Released By',
      flex: 0.5,
      minWidth: 100,
      valueFormatter: (value) => value ?? '—',
    },
    {
      field: 'actions',
      headerName: 'Action',
      flex: 0.5,
      minWidth: 110,
      sortable: false,
      renderCell: (p) => {
        if (p.row.released) {
          return <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#2a7b5a', fontSize: '0.75rem' }}>
            <LockOpenIcon sx={{ fontSize: 14 }} /> Released
          </Box>
        }
        if (!p.row.releasable) {
          return <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#b7b7b7', fontSize: '0.75rem' }}>
            <LockIcon sx={{ fontSize: 14 }} /> Locked
          </Box>
        }
        return (
          <Button
            size='small'
            variant='outlined'
            disabled={isSubmitting}
            onClick={() => onRelease(p.row)}
            sx={{
              fontFamily: 'AtlasGroteskRegular',
              fontSize: '0.72rem',
              textTransform: 'none',
              borderColor: '#2db47d',
              color: '#2db47d',
              '&:hover': { backgroundColor: '#e8f5e9', borderColor: '#145a4b', color: '#145a4b' },
            }}
          >
            Release
          </Button>
        )
      },
    },
  ]

  const gridStyle = {
    backgroundColor: theme.palette.common.white,
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: theme.palette.grey[300],
  }

  return (
    <Paper>
      <DataGrid
        sx={gridStyle}
        rows={rows}
        columns={columns}
        pageSizeOptions={[25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
        disableColumnFilter
        disableRowSelectionOnClick
        autoHeight
      />
    </Paper>
  )
}

HoldDetailsTable.propTypes = {
  holds: PropTypes.arrayOf(PropTypes.object).isRequired,
  onRelease: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
}

export default HoldDetailsTable
